import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const PRIVACY_STORAGE_KEY = "mmj_privacy_mode";
const HIDDEN_TOKEN = "....";
const NAME_POOL = [
  "Aarav",
  "Isha",
  "Vihaan",
  "Meera",
  "Reyansh",
  "Anaya",
  "Kabir",
  "Diya",
  "Arjun",
  "Myra",
  "Advik",
  "Sara",
];

const PrivacyContext = createContext({
  privacyEnabled: false,
  setPrivacyEnabled: () => {},
  togglePrivacy: () => {},
  maskName: (value) => value || "—",
  maskNumber: (value, options) => formatVisibleNumber(value, options),
  maskText: (value) => value || "—",
  maskCode: (value) => value || "—",
  maskEmail: (value) => value || "—",
  maskPhone: (value) => value || "—",
});

function hashValue(value) {
  const input = String(value || "");
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function formatVisibleNumber(value, { prefix = "", suffix = "", empty = "—" } = {}) {
  if (value === null || value === undefined || value === "") return empty;
  return `${prefix}${value}${suffix}`;
}

function buildAlias(value) {
  const hash = hashValue(value);
  const baseName = NAME_POOL[hash % NAME_POOL.length];
  const serial = 100 + (hash % 900);
  return `${baseName} ${serial}`;
}

export function PrivacyProvider({ children }) {
  const [privacyEnabled, setPrivacyEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(PRIVACY_STORAGE_KEY) === "1";
  });

  useEffect(() => {
    localStorage.setItem(PRIVACY_STORAGE_KEY, privacyEnabled ? "1" : "0");
  }, [privacyEnabled]);

  const value = useMemo(
    () => ({
      privacyEnabled,
      setPrivacyEnabled,
      togglePrivacy: () => setPrivacyEnabled((prev) => !prev),
      maskName: (rawValue, { empty = "—" } = {}) => {
        const value = String(rawValue || "").trim();
        if (!value) return empty;
        if (!privacyEnabled) return value;
        return buildAlias(value);
      },
      maskNumber: (rawValue, options = {}) => {
        if (!privacyEnabled) return formatVisibleNumber(rawValue, options);
        const {
          prefix = "",
          suffix = "",
          empty = "—",
        } = options;
        if (rawValue === null || rawValue === undefined || rawValue === "") return empty;
        return `${prefix}${HIDDEN_TOKEN}${suffix}`;
      },
      maskText: (rawValue, { empty = "—", hiddenLabel = "Hidden" } = {}) => {
        const value = String(rawValue || "").trim();
        if (!value) return empty;
        if (!privacyEnabled) return value;
        return hiddenLabel;
      },
      maskCode: (rawValue, { empty = "—", prefix = "" } = {}) => {
        const value = String(rawValue || "").trim();
        if (!value) return empty;
        if (!privacyEnabled) return value;
        const serial = 1000 + (hashValue(value) % 9000);
        return `${prefix}${serial}`;
      },
      maskEmail: (rawValue, { empty = "—" } = {}) => {
        const value = String(rawValue || "").trim();
        if (!value) return empty;
        if (!privacyEnabled) return value;
        return `hidden${hashValue(value) % 1000}@private.local`;
      },
      maskPhone: (rawValue, { empty = "—" } = {}) => {
        const value = String(rawValue || "").trim();
        if (!value) return empty;
        if (!privacyEnabled) return value;
        return `${HIDDEN_TOKEN}${HIDDEN_TOKEN}`;
      },
    }),
    [privacyEnabled]
  );

  return (
    <PrivacyContext.Provider value={value}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy() {
  return useContext(PrivacyContext);
}
